import React, { cloneElement, createContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { countrySelected, getCountries, getOnlineAuctions, getUpcomingAuctions } from 'actions';
import TopSearchForm from 'components/Auctions/TopSearchForm';
import AuctionTile from 'components/Auctions/AuctionTile';
import TrustPilot from 'components/partials/TrustPilot';
import Pagination from 'components/partials/Pagination';
import AuctionFilters from 'components/Auctions/AuctionFilters';
import { Loading } from 'components/Loading';
import MissingBlock from 'components/partials/MissingBlock';
import { TopNav } from './TopNav';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import useSearch from './hooks/useSearch';
import useHandleRedirects from './hooks/useHandleRedirects';
import ShowingListings from './ShowingListings';
import { responsiveMapSizes } from 'helpers/constants/responsive';
import useWindowSize from 'hooks/useWindowSize';
import './styles.scss';

export const SearchContext = createContext({});

const OnlineSearch = (props) => {
    const width = useWindowSize().width;
    const {
        user_id,
        getUpcomingAuctions,
        getOnlineAuctions,
        getCountries,
        loading,
        coming_soon_loading,
        total_records,
        upcoming_total_records = 100,
        categories,
        filter_unit_contents,
        windowWidth,
        metrics,
        setStateName,
        locationUpdate,
        stateCode,
        loadingCity,
    } = props;
    const initialRender = useRef(true);
    const [showSidebar, setShowSidebar] = useState(false);

    const {
        searchState,
        setSearchState,
        sortOptions,
        onSortByChange,
        restartLoop,
        searchObjUpdated,
        redoSearch,
        fireCancel,
        initialSearchState,
        makeApiObj,
    } = useSearch({
        user_id,
        getOnlineAuctions,
        getUpcomingAuctions,
        initialRender,
    });
    useHandleRedirects({
        searchState,
    });

    const getChildByDisplayName = (displayName) => {
        const child = React.Children.map(props.children, (child => {
            // you can access displayName property by child.type.displayName
            if (child.type.displayName === displayName) return child;
            return null;
        })) || [];
        return child[0];
    };

    const is_loading = () => loading === true || coming_soon_loading === true;
    const renderSMTrustpilot = () => (
        windowWidth > 991
            ? null
            : (
                <div className="row text-center">
                    <div className="trust-pilot-container">
                        {/* small review box more information */}
                        <TrustPilot height="60px" template_id="5419b6ffb0d04a076446a9af" />
                    </div>
                </div>
            )
    );

    const renderLGTrustPilot = () => (
        windowWidth <= 991
            ? null
            : (
                <div className="trust-pilot-container-large">
                    {/* original trust pilot template but not as tall */}
                    <TrustPilot data-reviewnumber={10} height="300px" template_id="54d0e1d8764ea9078c79e6ee" />
                </div>
            )
    );

    const renderAuctionTiles = (auction_type) => {
        if (is_loading()) return null;

        const {
            [auction_type]: auctions = [],
        } = props;

        if (!(auctions || []).length) return <MissingBlock />;

        return auctions.map((auction, i) => (
            <AuctionTile
                key={auction.id || i}
                auction={auction}
                allowHorizontal
            />
        ));
    };

    const getAuctionLocations = (auction_type = 'auctions') => {
        const {
            [auction_type]: auctions = [],
        } = props;

        return !auctions || !auctions.length
            ? []
            : auctions.map(({
                facility: {
                    facility_id,
                    facility_name,
                    address,
                    city,
                    state,
                    zipcode,
                    marker: { lat = '', lng = '', position = '' } = {},
                }, 
            }) => ({
                isFacilityLink: true,
                link: `/facilities/homepage/${facility_id}`,
                facility_name,
                address,
                city,
                state,
                zipcode,
                lat,
                lng,
                position,
            }));
    };

    const setTab = active_tab => {
        let  url = '';
        if (searchState?.search_obj?.type == 'city' && typeof searchState?.search_obj?.term_city != 'undefined') {
            url+=`${searchState?.search_obj?.term_state.trim().toLowerCase()}/${searchState?.search_obj?.term_city.split(',')[0].trim().toLowerCase()}`;
        } else if (searchState?.search_obj?.type == 'state') {
            url += `${searchState.search_obj.term_state}`;
        }
        // If upcoming has less than 15 result, reset page result
        searchState.search_obj.upcoming_page_num = upcoming_total_records > searchState.search_obj.page_count ? searchState.search_obj.upcoming_page_num : 1;
        const propName = active_tab === 'active' ? 'auctions' : 'upcoming_auctions';
        setSearchState({
            ...searchState,
            active_tab,
            page_history: [...searchState.page_history, {
                tab: active_tab,
                page_num: active_tab === 'active' ? searchState.search_obj.page_num : searchState.search_obj.upcoming_page_num,
            }],
        });

        if (!props[propName].length) {
            restartLoop({type: active_tab});
        }
        window.history.pushState({}, '', `/auctions/${url}`);
    };

    const forceRefresh = () => {
        if (!localStorage.getItem('search_obj')) {
            setSearchState({
                ...initialSearchState,
            });
            localStorage.setItem('search_obj', JSON.stringify(initialSearchState.search_obj));
            redoSearch({
                search_obj_for_api: initialSearchState.search_obj,
            });
            fireCancel();
        }
    };

    forceRefresh();

    useEffect(() => {
        if (initialRender.current) {
            getCountries();
            initialRender.current = false;
        }
    }, []);

    useEffect(() => {
        if (setStateName) {
            if (searchState?.search_obj?.term_state_label) {
                setStateName(searchState?.search_obj?.term_state_label);
            }
        }
    }, [searchState.search_obj]);

    useEffect(() => {

        if (stateCode) {

            let stateObject = {};

            async function fetchStates() {

                const listStates = await getCountries();

                for (let i = 0; i < listStates.countries.length; i++) {

                    let countryStates = listStates.countries[i].states;

                    for (let j = 0; j < countryStates.length; j++) {
                        let state = countryStates[j];
                        stateObject[state.state_code.toLowerCase()] = state.state_name;
                    }
                }
            }

            fetchStates();

            setSearchState({
                ...searchState,
                search_obj: {
                    ...searchState.search_obj,
                    term_state: stateCode,
                    term_state_label: stateObject[stateCode],
                    type : 'state',
                },
                page_history: searchState.page_history.slice(0, searchState.page_history.length - 1),
            });

            setStateName(stateObject[stateCode]);

            redoSearch({
                search_obj_for_api: {
                    ...searchState.search_obj,
                    term_state: stateCode,
                    term_state_label: stateObject[stateCode],
                    type : 'state',
                },
            });
            fireCancel();
        }

    }, [stateCode]);

    useEffect(() => {

        if (locationUpdate) {
            const locationSplit = locationUpdate.split(', ');

            setSearchState({
                ...searchState,
                search_obj: {
                    ...searchState.search_obj,
                    term_city: locationUpdate,
                    term_state: locationSplit[1],
                    term_city_label: locationUpdate.toLowerCase(),
                },
                page_history: searchState.page_history.slice(0, searchState.page_history.length - 1),
            });

            redoSearch({
                search_obj_for_api: {
                    ...searchState.search_obj,
                    term_city: locationUpdate,
                    term_state: locationSplit[1],
                    term_city_label: locationUpdate.toLowerCase(),
                },
            });

            fireCancel();
        }

    }, [locationUpdate]);

    const handlePop = () => {
        if (searchState.page_history.length > 1) {
            const _page_num = searchState.active_tab === 'active' ? 'page_num' : 'upcoming_page_num';
            const i = searchState.page_history.length -2;

            setSearchState({
                ...searchState,
                active_tab: searchState.page_history[i].tab,
                search_obj: {
                    ...searchState.search_obj,
                    [_page_num]: searchState.page_history[i].page_num,
                },
                page_history: searchState.page_history.slice(0, searchState.page_history.length - 1),
            });
        }
    };

    useEffect(() => {
        window.addEventListener('popstate', handlePop);

        return () => window.removeEventListener('popstate', handlePop);
    }, [searchState.page_history.length]);

    const renderPanel = (tab, auctionType, totalRecords, pageNumKey) => (
        <div>
            <div className="auctions-container marginT10">
                <div className="d-flex justify-content-center flex-column flex-md-row flex-md-wrap w-100 align-items-center">
                    {renderAuctionTiles(auctionType)}
                </div>
            </div>
            <Row>
                <Col md={12} className="d-flex justify-content-end">
                    <ShowingListings />
                </Col>
                <Col md={12}>
                    <Pagination
                        perPage={searchState?.search_obj?.page_count}
                        dataCount={totalRecords}
                        currentPage={parseInt(searchState?.search_obj?.[pageNumKey]) || 1}
                        redoSearch={(page_num) => {
                            window.history.pushState({}, '', '/auctions');
                            setSearchState({
                                ...searchState,
                                page_history: [
                                    ...searchState.page_history,
                                    { tab, page_num },
                                ],
                                search_obj: { ...searchState.search_obj, [pageNumKey]: page_num },
                            });
                        }}
                    />
                </Col>
            </Row>
        </div>
    );

    return (
        <SearchContext.Provider
            value={{
                searchState,
                setSearchState,
                restartLoop,
                makeApiObj,
                ...props,
            }}
        >
            <TopSearchForm
                disabled={is_loading()}
                searchState={searchState}
                setSearchState={setSearchState}
                redoSearch={restartLoop}
                search_obj_updated={searchObjUpdated}
                search_obj={searchState.search_obj}
                getStateName={term_state_label => {
                    if (term_state_label !== searchState?.search_obj?.term_state_label) {
                        setSearchState({
                            ...searchState,
                            search_obj: {...searchState.search_obj, term_state_label},
                        });
                    }
                }}
                setCurrentState={state_name => {
                    setSearchState({
                        ...searchState,
                        search_obj: {...searchState.search_obj, state_name},
                    });
                }}
            />
            <Row className="marginT10 pl-2">
                <Col className={`marginB10 d-flex justify-content-${is_loading() ? 'center' : 'between'}`}>
                    {is_loading() ? <Loading /> : (
                        <>
                            <span className="small">
                                Total Auctions:
                                {' '}
                                {(searchState.active_tab === 'active' ? total_records : upcoming_total_records) || 0}
                            </span>
                            <div
                                className="filter-button d-lg-none"
                                onClick={() => setShowSidebar(!showSidebar)}
                            >
                                <span>
                                    <i className="fa fa-lg fa-filter marginR10" />
                                    <span className="filter-button-title">Filters</span>
                                </span>
                                <i className="fa fa-lg fa-caret-right" />
                            </div>
                        </>
                    )}
                </Col>
            </Row>
            <Row className="marginT10">
                {width > responsiveMapSizes.sm_max ?
                    <Col md={3} className="left-auction-search-filters">
                        <AuctionFilters
                            search_obj={searchState.search_obj}
                            updateMethod={searchObjUpdated}
                            auctionCategories={categories}
                            filterContents={filter_unit_contents}
                            redoSearch={() => {
                                setSearchState({
                                    ...searchState,
                                    search_obj: { ...searchState.search_obj, page_num: 1 },
                                });

                                restartLoop();
                            }}
                        />
                        {renderLGTrustPilot()}
                    </Col>
                    :
                    <AuctionFilters
                        showSidebar={showSidebar}
                        onHide={() => setShowSidebar(false)}
                        search_obj={searchState.search_obj}
                        updateMethod={searchObjUpdated}
                        auctionCategories={categories}
                        filterContents={filter_unit_contents}
                        redoSearch={() => {
                            setSearchState({
                                ...searchState,
                                search_obj: { ...searchState.search_obj, page_num: 1 },
                            });
                            restartLoop();
                        }}
                    />
                }

                {is_loading() ? null : (
                    <Col lg={9} xs={12}>
                        {getChildByDisplayName('TopContentBlock') &&
                            cloneElement(getChildByDisplayName('TopContentBlock'), {
                                searchState,
                                setSearchState,
                                metrics,
                                ...props,
                            })}
                        <TopNav
                            setTab={setTab}
                            disabled={loadingCity}
                            active_tab={searchState.active_tab}
                            getAuctionLocations={getAuctionLocations}
                            state={searchState}
                            sortOptions={sortOptions}
                            onSortByChange={onSortByChange}
                            sort_column_index={searchState.search_obj.sort_column_index}
                        />

                        <div className="tab-content marginT0">
                            {(searchState.active_tab === 'active' || searchState.active_tab === 'upcoming') &&
                                renderPanel(
                                    searchState.active_tab,
                                    `${searchState.active_tab === 'active' ? 'auctions' : 'upcoming_auctions'}`,
                                    searchState.active_tab === 'active' ? total_records : upcoming_total_records,
                                    `${searchState.active_tab === 'active' ? 'page_num' : 'upcoming_page_num'}`,
                                )
                            }
                        </div>

                        {getChildByDisplayName('AuctionsByState') &&
                            cloneElement(getChildByDisplayName('AuctionsByState'), {
                                searchState,
                                setSearchState,
                                metrics,
                                ...props,
                            })}

                        {renderSMTrustpilot()}
                    </Col>
                )}
            </Row>
        </SearchContext.Provider>
    );
};

const mapStateToProps = ({ auction, user: { user_id }, page: { windowWidth } }) => {
    const {
        auctions,
        online_search_loading: loading,
        categories,
        total_records,
        coming_soon_loading,
        upcoming_auctions,
        upcoming_total_records,
        filter_unit_contents,
        metrics,
    } = auction;

    return {
        auctions,
        loading,
        coming_soon_loading,
        categories,
        total_records,
        upcoming_auctions,
        upcoming_total_records,
        filter_unit_contents,
        windowWidth,
        metrics,
        user_id,
    };
};

const mapActionToProps = { getOnlineAuctions, getUpcomingAuctions, getCountries, countrySelected };

export default connect(mapStateToProps, mapActionToProps)(OnlineSearch);
